import type { OutboxEntry, ToolOutput } from '@hugo/core'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useRef } from 'react'
import { toast } from '@/components/ui/toast'
import { queryKeys, useRunTool } from './items'

type OutboxListData = ToolOutput<'outbox.list'>

/**
 * The toast after an approval. Approving usually answers `sending`: the writes gateway sends in
 * the background (or, with writes off, records a dry run) and the Outbox follows live.
 */
function announce(entry: OutboxEntry, writesEnabled: boolean) {
  const to = entry.recipients.join(', ')
  const dryRun = {
    title: 'Approved',
    description: 'Sending is off on this server: recorded as “Would have been sent”.',
    type: 'info',
    timeout: 4000,
  }
  if (entry.status === 'sent') {
    toast.add({ title: 'Sent', description: `To ${to}`, type: 'success', timeout: 4000 })
  } else if (entry.status === 'dry_run' || (entry.status === 'sending' && !writesEnabled)) {
    toast.add(dryRun)
  } else if (entry.status === 'sending') {
    toast.add({ title: 'Sending…', description: `To ${to}`, timeout: 3000 })
  } else if (entry.status === 'failed') {
    toast.add({
      title: 'Could not send',
      description: entry.error ?? 'Sending failed.',
      type: 'error',
    })
  } else if (entry.status === 'withdrawn') {
    // hugo found it no longer needed while it waited (they replied, the item was done).
    const why = entry.error ? `${entry.error.charAt(0).toUpperCase()}${entry.error.slice(1)}.` : ''
    toast.add({
      title: 'Not sent',
      description: `${why} hugo withdrew it: it is no longer needed.`.trim(),
      type: 'info',
      timeout: 5000,
    })
  }
}

/**
 * Approve or discard outbox drafts. The entry leaves "Waiting for approval" right away and comes
 * back if the call fails; an entry already on its way is never sent a second time.
 */
export function useOutboxActions() {
  const queryClient = useQueryClient()
  const { run } = useRunTool()
  const inflight = useRef(new Set<string>())

  return useMemo(() => {
    const decide = async <T>(entry: OutboxEntry, call: () => Promise<T | null>) => {
      if (inflight.current.has(entry.id)) return null
      inflight.current.add(entry.id)
      const key = queryKeys.outboxList('pending')
      try {
        await queryClient.cancelQueries({ queryKey: key })
        const snapshot = queryClient.getQueryData<OutboxListData>(key)
        queryClient.setQueryData<OutboxListData>(key, (old) =>
          old ? { ...old, entries: old.entries.filter((e) => e.id !== entry.id) } : old,
        )
        const result = await call()
        if (result === null && snapshot) queryClient.setQueryData(key, snapshot)
        return result
      } finally {
        inflight.current.delete(entry.id)
      }
    }

    return {
      /** `writesEnabled`: whether this server sends at all (from outbox.list). */
      approve: (entry: OutboxEntry, writesEnabled: boolean) =>
        decide(entry, async () => {
          const result = await run('outbox.approve', { id: entry.id }, { failed: 'Could not send' })
          if (result) announce(result.entry, writesEnabled)
          return result
        }),
      discard: (entry: OutboxEntry) =>
        decide(entry, () =>
          run(
            'outbox.discard',
            { id: entry.id },
            { done: 'Draft discarded', failed: 'Could not discard' },
          ),
        ),
    }
  }, [queryClient, run])
}
